<template>
    <main id="js-page-content" class="form-types-v2" role="main">
        <portal to="ariane">
            <div class="tbv2-page-title">{{ $t('form_types.title') }}</div>
        </portal>

        <header class="form-types-hero">
            <h1 class="form-types-hero-title">{{ $t('form_types.title') }}</h1>
            <p class="form-types-hero-sub">{{ subtitleLabel }}</p>
        </header>

        <section class="form-types-card">
            <div class="form-types-toolbar">
                <div class="form-types-toolbar-desc">{{ $t('form_types.subtitle') }}</div>
                <button
                    v-if="can('form-types.create')"
                    type="button"
                    class="form-types-btn-create"
                    @click="openCreateModal"
                >
                    <i class="fal fa-plus"></i>
                    <span>{{ $t('form_types.create') }}</span>
                </button>
            </div>

            <BmvTable
                :columns="tableColumns"
                :data="formTypes"
                :loading="loading"
            >
                <template #cell-id="{ row }">
                    <span class="ft-id">#{{ row.id }}</span>
                </template>

                <template #cell-name="{ row }">
                    <router-link :to="`/form-types/${row.id}`" class="ft-link">
                        {{ row.name }}
                    </router-link>
                </template>

                <template #cell-purpose="{ row }">
                    <span class="ft-pill" :class="purposeClass(row.purpose)">
                        {{ purposeLabel(row.purpose) }}
                    </span>
                </template>

                <template #cell-listable="{ row }">
                    <i
                        class="fal"
                        :class="row.listable ? 'fa-check-circle ft-yes' : 'fa-times-circle ft-no'"
                    ></i>
                </template>

                <template #cell-forms_count="{ row }">
                    <span class="ft-count">{{ row.forms_count || 0 }}</span>
                </template>

                <template #cell-actions="{ row }">
                    <div class="ft-actions">
                        <button
                            v-if="can('form-types.update')"
                            class="ft-btn ft-btn--ghost"
                            :title="$t('common.edit')"
                            @click="openEditModal(row)"
                        >
                            <i class="fal fa-pen"></i>
                        </button>
                        <button
                            v-if="can('form-types.delete')"
                            class="ft-btn ft-btn--danger"
                            :title="$t('common.delete') || 'Supprimer'"
                            @click="confirmDelete(row)"
                        >
                            <i class="fal fa-trash"></i>
                        </button>
                    </div>
                </template>
            </BmvTable>
        </section>

        <BmvModal v-model="modalOpen" :title="modalTitle" size="md">
            <div class="ft-form" v-if="editingFormType">
                <div v-for="lang in langs" :key="lang" class="ft-lang-block">
                    <span class="ft-lang-badge">{{ lang }}</span>
                    <div class="ft-field">
                        <label class="ft-label">{{ $t('form_types.name') }}</label>
                        <input
                            v-model="editingFormType.translations[lang].name"
                            type="text"
                            class="ft-input"
                        />
                    </div>
                    <div class="ft-field">
                        <label class="ft-label">{{ $t('form_types.description') }}</label>
                        <textarea
                            v-model="editingFormType.translations[lang].description"
                            class="ft-input ft-textarea"
                            rows="2"
                        ></textarea>
                    </div>
                </div>

                <div class="ft-field">
                    <label class="ft-label">{{ $t('form_types.purpose') }}</label>
                    <div class="ft-select-wrapper">
                        <select v-model="editingFormType.purpose" class="ft-input ft-select">
                            <option v-for="p in purposes" :key="p" :value="p">
                                {{ $t(`form_types.purposes.${p}`) }}
                            </option>
                            <option value="__custom__">
                                {{ $t('form_types.purposes.custom') }}
                            </option>
                        </select>
                        <i class="fal fa-chevron-down ft-select-caret"></i>
                    </div>
                </div>

                <div v-if="editingFormType.purpose === '__custom__' || isCustomPurpose" class="ft-field">
                    <label class="ft-label">{{ $t('form_types.custom_purpose') }}</label>
                    <input
                        v-model="customPurpose"
                        type="text"
                        class="ft-input"
                        :placeholder="$t('form_types.custom_purpose_placeholder')"
                    />
                </div>

                <div class="ft-toggles">
                    <label class="ft-toggle-row">
                        <div class="ft-toggle-text">
                            <span class="ft-toggle-label">{{ $t('form_types.active') }}</span>
                            <span class="ft-toggle-hint">{{ $t('form_types.active_hint') }}</span>
                        </div>
                        <span class="ft-switch" :class="{ 'ft-switch--on': editingFormType.active }">
                            <input v-model="editingFormType.active" type="checkbox" class="ft-switch-input" />
                            <span class="ft-switch-knob"></span>
                        </span>
                    </label>
                    <label class="ft-toggle-row">
                        <div class="ft-toggle-text">
                            <span class="ft-toggle-label">{{ $t('form_types.listable') }}</span>
                            <span class="ft-toggle-hint">{{ $t('form_types.listable_hint') }}</span>
                        </div>
                        <span class="ft-switch" :class="{ 'ft-switch--on': editingFormType.listable }">
                            <input v-model="editingFormType.listable" type="checkbox" class="ft-switch-input" />
                            <span class="ft-switch-knob"></span>
                        </span>
                    </label>
                </div>
            </div>

            <template #footer>
                <button type="button" class="ft-btn-action ft-btn-action--ghost" @click="closeModal">
                    {{ $t('common.cancel') }}
                </button>
                <button
                    type="button"
                    class="ft-btn-action ft-btn-action--primary"
                    :disabled="saving"
                    @click="saveFormType"
                >
                    <i v-if="saving" class="fas fa-circle-notch fa-spin"></i>
                    <i v-else class="fal fa-check"></i>
                    {{ $t('common.save') }}
                </button>
            </template>
        </BmvModal>
    </main>
</template>

<script>
import BmvTable from '@/components/UI/BmvTable'
import BmvModal from '@/components/UI/v2/BmvModal.vue'
import { mapActions, mapState } from 'vuex'

export default {
    name: 'FormTypes',
    components: { BmvTable, BmvModal },
    data() {
        return {
            modalOpen: false,
            formTypes: [],
            loading: true,
            saving: false,
            editingFormType: null,
            isCreating: false,
            customPurpose: '',
            purposes: ['ticket', 'survey'],
            tableColumns: [
                { field: 'id', label: 'ID', width: '60px' },
                { field: 'name', label: this.$t('form_types.name') },
                { field: 'purpose', label: this.$t('form_types.purpose') },
                { field: 'listable', label: this.$t('form_types.listable'), width: '90px', align: 'center' },
                { field: 'forms_count', label: this.$t('form_types.forms_count'), width: '90px', align: 'center' },
                { field: 'actions', label: this.$t('form_types.actions'), width: '110px', align: 'center' }
            ]
        }
    },
    computed: {
        ...mapState({ langs: (state) => state.langs }),
        permissions() {
            return this.$store.state.auth.permissions
                || JSON.parse(localStorage.getItem('permissions') || '[]')
        },
        isCustomPurpose() {
            if (!this.editingFormType) return false
            return this.editingFormType.purpose !== '__custom__'
                && !this.purposes.includes(this.editingFormType.purpose)
                && this.editingFormType.purpose
        },
        modalTitle() {
            return this.isCreating
                ? this.$t('form_types.create_title')
                : this.$t('form_types.edit_title')
        },
        subtitleLabel() {
            const n = this.formTypes.length
            if (n === 0) return this.$t('form_types.count_zero')
            const formatted = new Intl.NumberFormat(this.$i18n.locale).format(n)
            const key = n === 1 ? 'form_types.count_one' : 'form_types.count_other'
            return this.$t(key, { count: formatted })
        }
    },
    created() {
        this.loadFormTypes()
    },
    methods: {
        ...mapActions('form', ['getFormTypes', 'createFormType', 'updateFormType', 'deleteFormType']),
        can(permission) {
            return this.permissions.includes(permission)
        },
        loadFormTypes() {
            this.loading = true
            this.getFormTypes()
                .then((response) => {
                    this.formTypes = response.data.types || response.data
                })
                .catch((error) => {
                    this.$store.dispatch('dispatchError', error)
                })
                .finally(() => {
                    this.loading = false
                })
        },
        openCreateModal() {
            const translations = {}
            this.langs.forEach((lang) => {
                translations[lang] = { name: '', description: '' }
            })
            this.editingFormType = { active: true, purpose: 'ticket', listable: true, translations }
            this.customPurpose = ''
            this.isCreating = true
            this.modalOpen = true
        },
        openEditModal(formType) {
            const translations = {}
            this.langs.forEach((lang) => {
                if (formType.translations) {
                    const t = formType.translations.find((tr) => tr.locale === lang)
                    translations[lang] = {
                        name: t ? t.name : '',
                        description: t ? (t.description || '') : ''
                    }
                } else {
                    translations[lang] = { name: formType.name || '', description: '' }
                }
            })
            const purpose = formType.purpose || 'ticket'
            const isKnown = this.purposes.includes(purpose)
            this.customPurpose = isKnown ? '' : purpose
            this.editingFormType = {
                id: formType.id,
                active: formType.active !== undefined ? formType.active : true,
                purpose: isKnown ? purpose : '__custom__',
                listable: formType.listable !== undefined ? formType.listable : true,
                translations
            }
            this.isCreating = false
            this.modalOpen = true
        },
        closeModal() {
            this.modalOpen = false
            this.editingFormType = null
            this.isCreating = false
            this.customPurpose = ''
        },
        saveFormType() {
            this.saving = true
            const payload = { ...this.editingFormType }
            if (payload.purpose === '__custom__') {
                payload.purpose = this.customPurpose
            }
            const action = this.isCreating ? 'createFormType' : 'updateFormType'
            this[action](payload)
                .then(() => {
                    this.$store.dispatch('toast', {
                        type: 'success',
                        title: this.$t('success.title')
                    })
                    this.closeModal()
                    this.loadFormTypes()
                })
                .catch((error) => {
                    this.$store.dispatch('dispatchError', error)
                })
                .finally(() => {
                    this.saving = false
                })
        },
        purposeLabel(purpose) {
            const key = `form_types.purposes.${purpose || 'other'}`
            const translated = this.$t(key)
            return translated !== key ? translated : purpose
        },
        purposeClass(purpose) {
            const map = {
                ticket: 'ft-pill--cyan',
                survey: 'ft-pill--purple',
                other: 'ft-pill--neutral'
            }
            return map[purpose] || 'ft-pill--amber'
        },
        confirmDelete(formType) {
            this.$bmvDialog.confirm({
                variant: 'danger',
                title: this.$t('form_types.delete_confirm'),
                confirmText: this.$t('common.delete') || 'Supprimer'
            }).then((ok) => {
                if (!ok) return
                this.deleteFormType(formType.id)
                    .then(() => {
                        this.$store.dispatch('toast', {
                            type: 'success',
                            title: this.$t('success.title')
                        })
                        this.loadFormTypes()
                    })
                    .catch((error) => {
                        this.$store.dispatch('dispatchError', error)
                    })
            })
        }
    }
}
</script>

<style scoped>
.form-types-v2 {
    width: 100%;
    padding: 24px 32px 48px;
    font-family: var(--v2-font-sans);
    color: var(--v2-text-2);
}

.tbv2-page-title {
    font-size: 0.95rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
    letter-spacing: -0.005em;
}

.form-types-hero {
    margin-bottom: var(--v2-space-5);
}

.form-types-hero-title {
    margin: 0 0 6px;
    font-size: 1.75rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
    letter-spacing: -0.02em;
    line-height: 1.15;
}

.form-types-hero-sub {
    margin: 0;
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-3);
    line-height: 1.5;
}

.form-types-card {
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    overflow: hidden;
}

.form-types-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--v2-space-3);
    padding: var(--v2-space-4) var(--v2-space-5);
    border-bottom: 1px solid var(--v2-border);
}

.form-types-toolbar-desc {
    flex: 1;
    font-size: var(--v2-text-xs-size);
    color: var(--v2-text-3);
    min-width: 0;
}

.form-types-btn-create {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 32px;
    padding: 0 14px;
    border-radius: var(--v2-radius-md);
    background: var(--v2-primary);
    color: #fff !important;
    font-size: var(--v2-text-xs-size);
    font-weight: var(--v2-font-weight-semibold);
    border: none;
    cursor: pointer;
    transition: background var(--v2-transition-fast), box-shadow var(--v2-transition-fast);
    white-space: nowrap;
    flex-shrink: 0;
}

.form-types-btn-create i {
    font-size: 0.75rem;
    color: #fff !important;
}

.form-types-btn-create:hover {
    background: var(--v2-primary-hover, #0e7490);
    box-shadow: var(--v2-shadow-sm);
}

.ft-id {
    font-size: 0.72rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-4);
    font-variant-numeric: tabular-nums;
}

.ft-link {
    color: var(--v2-text-1);
    font-weight: var(--v2-font-weight-semibold);
    text-decoration: none !important;
    transition: color var(--v2-transition-fast);
}

.ft-link:hover {
    color: var(--v2-primary);
}

.ft-pill {
    display: inline-flex;
    align-items: center;
    padding: 2px 10px;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: var(--v2-font-weight-semibold);
}

.ft-pill--cyan { background: rgba(8, 145, 178, 0.12); color: #0891B2; }
.ft-pill--purple { background: rgba(139, 92, 246, 0.12); color: #7c3aed; }
.ft-pill--neutral { background: var(--v2-surface-2); color: var(--v2-text-3); }
.ft-pill--amber { background: rgba(245, 158, 11, 0.14); color: #b45309; }

.ft-yes { color: var(--v2-success); font-size: 1rem; }
.ft-no { color: var(--v2-text-4); font-size: 1rem; }

.ft-count {
    font-variant-numeric: tabular-nums;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
}

.ft-actions {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.ft-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: var(--v2-radius-sm);
    border: 1px solid var(--v2-border);
    cursor: pointer;
    transition: all var(--v2-transition-fast);
    background: var(--v2-surface-0);
    color: var(--v2-text-2);
}

.ft-btn i {
    font-size: 0.75rem;
}

.ft-btn--ghost:hover {
    border-color: var(--v2-primary);
    color: var(--v2-primary);
    background: var(--v2-primary-soft);
}

.ft-btn--danger:hover {
    border-color: var(--v2-danger);
    color: var(--v2-danger);
    background: rgba(239, 68, 68, 0.08);
}

/* Modal form */
.ft-form {
    display: flex;
    flex-direction: column;
    gap: 18px;
}

.ft-lang-block {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 14px;
    background: var(--v2-surface-1);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
}

.ft-lang-badge {
    align-self: flex-start;
    display: inline-flex;
    padding: 2px 8px;
    border-radius: var(--v2-radius-sm);
    background: var(--v2-primary-soft);
    color: var(--v2-primary);
    font-size: 0.66rem;
    font-weight: var(--v2-font-weight-bold);
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.ft-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.ft-label {
    font-size: var(--v2-text-xs-size);
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-2);
    margin: 0;
}

.ft-input {
    width: 100%;
    box-sizing: border-box;
    height: 38px;
    padding: 0 12px;
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-1);
    font-family: inherit;
    outline: none;
    transition: border-color var(--v2-transition-fast), box-shadow var(--v2-transition-fast);
}

.ft-textarea {
    height: auto;
    min-height: 60px;
    padding: 8px 12px;
    resize: vertical;
}

.ft-input:focus {
    border-color: var(--v2-primary);
    box-shadow: 0 0 0 3px rgba(8, 145, 178, 0.12);
}

.ft-select-wrapper {
    position: relative;
}

.ft-select {
    padding-right: 34px;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    cursor: pointer;
    background: var(--v2-surface-0);
}

.ft-select-caret {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 0.75rem;
    color: var(--v2-text-3);
    pointer-events: none;
}

/* Toggles */
.ft-toggles {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.ft-toggle-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--v2-space-3);
    padding: 12px 14px;
    background: var(--v2-surface-1);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    cursor: pointer;
    transition: border-color var(--v2-transition-fast);
}

.ft-toggle-row:hover {
    border-color: var(--v2-border-strong);
}

.ft-toggle-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}

.ft-toggle-label {
    font-size: var(--v2-text-sm-size);
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
}

.ft-toggle-hint {
    font-size: var(--v2-text-xs-size);
    color: var(--v2-text-3);
}

.ft-switch {
    position: relative;
    width: 38px;
    height: 22px;
    border-radius: 999px;
    background: var(--v2-border-strong);
    flex-shrink: 0;
    transition: background var(--v2-transition-fast);
}

.ft-switch--on {
    background: var(--v2-primary);
}

.ft-switch-input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
    width: 0;
    height: 0;
}

.ft-switch-knob {
    position: absolute;
    top: 3px;
    left: 3px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #fff;
    transition: transform var(--v2-transition-fast);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

.ft-switch--on .ft-switch-knob {
    transform: translateX(16px);
}

/* Modal footer buttons */
.ft-btn-action {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 36px;
    padding: 0 16px;
    border-radius: var(--v2-radius-md);
    font-size: var(--v2-text-sm-size);
    font-weight: var(--v2-font-weight-semibold);
    border: 1px solid transparent;
    cursor: pointer;
    transition: all var(--v2-transition-fast);
    font-family: inherit;
}

.ft-btn-action i {
    font-size: 0.8rem;
}

.ft-btn-action--ghost {
    background: transparent;
    border-color: var(--v2-border);
    color: var(--v2-text-2);
}

.ft-btn-action--ghost:hover {
    background: var(--v2-surface-2);
    color: var(--v2-text-1);
}

.ft-btn-action--primary {
    background: var(--v2-primary);
    color: #fff !important;
}

.ft-btn-action--primary i {
    color: #fff !important;
}

.ft-btn-action--primary:hover:not(:disabled) {
    background: var(--v2-primary-hover, #0e7490);
    box-shadow: var(--v2-shadow-sm);
}

.ft-btn-action:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}

@media (max-width: 900px) {
    .form-types-v2 {
        padding: 16px 18px 32px;
    }

    .form-types-hero-title {
        font-size: 1.375rem;
    }

    .form-types-toolbar {
        flex-direction: column;
        align-items: stretch;
        padding: var(--v2-space-3) var(--v2-space-4);
    }

    .form-types-btn-create {
        justify-content: center;
    }
}
</style>
